import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, User as UserIcon, Eye, EyeSlash, X, ChefHat } from "@phosphor-icons/react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../lib/auth";

export default function LoginModal() {
  const { loginOpen, closeLogin, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const userRef = useRef(null);

  useEffect(() => {
    if (loginOpen) {
      setError("");
      setSubmitting(false);
      setTimeout(() => userRef.current?.focus(), 80);
    }
  }, [loginOpen]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      // Mobile keyboards often add stray whitespace via autocomplete; trim it.
      await login(username.trim(), password);
      setUsername("");
      setPassword("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid username or password";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {loginOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="login-modal"
        >
          <motion.div
            className="absolute inset-0 bg-[#0A3B24]/70 backdrop-blur-sm"
            onClick={closeLogin}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.22 }}
            className="relative w-full max-w-md rounded-xl bg-white shadow-2xl border border-[#D4AF37]/40 overflow-hidden"
          >
            <button
              type="button"
              onClick={closeLogin}
              className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#F3F4F6]"
              aria-label="Close login"
              data-testid="login-modal-close"
            >
              <X size={16} weight="bold" />
            </button>

            <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white px-6 py-6">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center">
                  <ChefHat size={22} weight="duotone" color="#D4AF37" />
                </div>
                <div>
                  <div className="text-[#D4AF37] text-[10px] tracking-[0.25em] uppercase font-semibold">Admin Access</div>
                  <h2 className="font-display text-xl tracking-tight">Sign in to KOSTRA</h2>
                </div>
              </div>
              <p className="text-xs text-white/70 mt-3">
                The calculator, exports, and saved recipes are restricted. Visitors may browse the app — admin login is required to interact.
              </p>
            </div>

            <form className="px-6 py-5 space-y-4" onSubmit={onSubmit} data-testid="login-form">
              <div>
                <Label htmlFor="login-username" className="kostra-data-label mb-1.5 block">Admin ID</Label>
                <div className="relative">
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
                  <Input
                    id="login-username"
                    ref={userRef}
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    inputMode="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter admin ID"
                    className="pl-9 h-11 border-2 focus:border-[#0A3B24]"
                    data-testid="login-username"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="login-password" className="kostra-data-label mb-1.5 block">Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
                  <Input
                    id="login-password"
                    type={show ? "text" : "password"}
                    autoComplete="current-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="pl-9 pr-10 h-11 border-2 focus:border-[#0A3B24]"
                    data-testid="login-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#0A3B24] h-7 w-7 inline-flex items-center justify-center rounded"
                    aria-label="Toggle password visibility"
                    data-testid="login-password-toggle"
                  >
                    {show ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="rounded-md bg-[#FBE8E8] border border-[#DC2626]/30 text-[#991B1B] text-sm px-3 py-2"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold tracking-wide"
                data-testid="login-submit"
              >
                {submitting ? "Signing in..." : "Sign in"}
              </Button>

              <p className="text-[11px] text-[#4B5563] text-center">
                Browsing only? <button type="button" onClick={closeLogin} className="text-[#0A3B24] font-semibold underline-offset-2 hover:underline" data-testid="login-keep-browsing">Keep browsing</button>
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
